import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ListChecks, Plus, X } from "lucide-react";

function ChipInput({ label, chips, setChips, testid, placeholder }) {
    const [draft, setDraft] = useState("");
    const add = () => {
        const v = draft.trim();
        if (!v) return;
        if (!chips.includes(v)) setChips([...chips, v]);
        setDraft("");
    };
    const remove = (i) => setChips(chips.filter((_, idx) => idx !== i));
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder={placeholder}
                    className="rounded-none"
                    data-testid={`${testid}-input`}
                />
                <Button type="button" onClick={add} variant="outline" className="rounded-none h-10" data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></Button>
            </div>
            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
                    {chips.map((c, i) => (
                        <span key={i} className="inline-flex items-center gap-1 bg-secondary/80 border border-border px-2 py-0.5 text-xs">
                            {c}
                            <button type="button" onClick={() => remove(i)} className="text-foreground/50 hover:text-rose-700" data-testid={`${testid}-remove-${i}`}>
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function BulkPushSkillsDialog({ open, candidateIds, onClose, onDone }) {
    const [hard, setHard] = useState([]);
    const [soft, setSoft] = useState([]);
    const [critical, setCritical] = useState([]);
    const [saving, setSaving] = useState(false);

    if (!open) return null;

    const submit = async () => {
        if (!candidateIds.length) { toast.error("Select at least one candidate"); return; }
        if (!(hard.length || soft.length || critical.length)) {
            toast.error("Add at least one skill"); return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/candidates/bulk-push-skills", {
                candidate_ids: candidateIds,
                hard_skills: hard, soft_skills: soft, critical,
            });
            toast.success(`Pushed to ${data.candidates_updated} candidate${data.candidates_updated === 1 ? "" : "s"} · ${data.interviews_updated} upcoming interview${data.interviews_updated === 1 ? "" : "s"} updated`);
            onDone?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-xl rounded-none border border-border" data-testid="bulk-push-skills-dialog">
                <DialogTitle className="font-display flex items-center gap-2">
                    <ListChecks className="w-5 h-5 text-ikb" /> Push rating skills to {candidateIds.length} candidate{candidateIds.length === 1 ? "" : "s"}
                </DialogTitle>
                <DialogDescription>
                    These skills appear on the interviewer rating form for every upcoming AND future interview round of the selected candidates.
                </DialogDescription>
                <div className="space-y-4 mt-3">
                    <ChipInput label="Hard skills" chips={hard} setChips={setHard} testid="bps-hard" placeholder="e.g. Python, SQL" />
                    <ChipInput label="Soft skills" chips={soft} setChips={setSoft} testid="bps-soft" placeholder="e.g. Communication" />
                    <ChipInput label="Critical / role-specific" chips={critical} setChips={setCritical} testid="bps-critical" placeholder="e.g. System design" />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="bps-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving || !candidateIds.length} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="bps-save">
                        {saving ? "Pushing…" : "Push skills"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
